import React, { useEffect, useState } from 'react';
import { Props } from './Current.types';
import { Box, Columns } from 'modules/af_cm_ui_kit/components';
import { INPUTS } from 'modules/cm/fields/types';
import { FormFields, validationSchema } from 'modules/cm/form/Applicant/CurrentIncome';
import { BUSINESS_TYPES, FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { useDispatch, useSelector } from 'react-redux';
import { SummaryBox } from 'modules/cm/pages/CaseDetailsPage/partials';
import formatCurrencyValue from 'utils/formatCurrency';
import RentalIncomeFields from './incomeFields/RentalIncomeFields';
import FosteringIncomeFields from './incomeFields/FosteringIncomeFields';
import BenefitIncomeFields from './incomeFields/BenefitIncomeFields';
import InvestmentsIncomeFields from './incomeFields/InvestmentsIncomeFields';
import MaintenanceIncomeFields from './incomeFields/MaintenanceIncomeFields';
import OtherIncomeFields from './incomeFields/OtherIncomeFields';
import { Nullable } from 'utils/types';
import styles from './Current.module.scss';
import Sticky from 'components/Sticky/Sticky';
import { State } from 'modules/cm/store/cases/types';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../../form/yup/reactHookFormResolver';
import { updateApplicant } from '../../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../../hooks/useSaveButtonClicked';
import { saveCase } from '../../../../../../store/cases/actions';
import * as subPages from '../../../../../../store/subPages';
import { SUBPAGES } from '../../../../../../store/subPages/types';
import CheckDirty from '../../../../../../components/CheckDirty/CheckDirty';

const businessTypeFieldsMap: Map<string, FIELDS[]> = new Map<BUSINESS_TYPES, FIELDS[]>([
  [
    BUSINESS_TYPES.LIMITED_COMPANY,
    [FIELDS.PERCENTAGE_OF_BUSINESS_OWNED, FIELDS.LIMITED_COMPANY_SALARY, FIELDS.LIMITED_COMPANY_DIVIDENDS],
  ],
  [BUSINESS_TYPES.PARTNERSHIP, [FIELDS.PERCENTAGE_OF_BUSINESS_OWNED, FIELDS.NET_PROFIT]],
  [BUSINESS_TYPES.SOLE_TRADER, [FIELDS.NET_PROFIT]],
]);

const Current: React.FC<Props> = ({
  modulePrefix,
  clearErrors,
  getErrors,
  applicantIndex,
  caseId,
  initialData,
  applicantId,
  currentIncome,
  resources,
}) => {
  const dispatch = useDispatch();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const methods = useForm<Props['currentIncome']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { isDirty },
    reset,
  } = methods;

  useEffect(() => {
    reset(currentIncome, {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [currentIncome, applicantIndex, setCurrentApplicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const currentIncomeReadonly = useSelector(({ currentCase }: { currentCase: State }) => {
    return currentCase.readonlyFields.applicants[applicantIndex].currentIncome;
  });

  useEffect(() => {
    if (Number(currentIncomeReadonly.currentIncomeTotal) > 0 && clearErrors && getErrors(FIELDS.TOTAL).length > 0) {
      clearErrors(FIELDS.TOTAL);
    }
  }, [clearErrors, getErrors, currentIncomeReadonly]);

  const getSummaryErrorMessage = (fieldName: string) => getErrors(fieldName).join(' ');

  return (
    <>
      <Sticky top={202}>
        <SummaryBox
          data={[
            {
              title: 'Employed total',
              value: formatCurrencyValue(Number(currentIncomeReadonly.currentIncomeEmployedTotal)),
              errorMessage: getSummaryErrorMessage(FIELDS.EMPLOYED_TOTAL),
            },
            {
              title: 'Self-employed total ',
              value: formatCurrencyValue(Number(currentIncomeReadonly.currentIncomeSelfEmployedTotal)),
              errorMessage: getSummaryErrorMessage(FIELDS.SELF_EMPLOYED_TOTAL),
            },
            {
              title: 'Other total',
              value: formatCurrencyValue(Number(currentIncomeReadonly.currentIncomeOtherTotal)),
              errorMessage: getSummaryErrorMessage(FIELDS.OTHER_TOTAL),
            },
            {
              title: 'Pension total',
              value: formatCurrencyValue(Number(currentIncomeReadonly.currentIncomePensionIncome)),
              errorMessage: getSummaryErrorMessage(FIELDS.CURRENT_INCOME_PENSION),
            },
            {
              title: 'Total',
              value: formatCurrencyValue(Number(currentIncomeReadonly.currentIncomeTotal)),
              errorMessage: getSummaryErrorMessage(FIELDS.TOTAL),
            },
          ]}
        />
      </Sticky>

      <FormProvider<Props['currentIncome']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(
                applicantIndex,
                SUBPAGES.APPLICANT_INCOME_CURRENT,
                isDirty,
              ),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <div className={styles.marginTop}>
            <Columns spacing={4}>
              <>
                <Box title="Employed Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.CURRENCY}
                    field={FormFields[FIELDS.BASIC_GROSS_ANNUAL_SALARY]}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.CURRENCY}
                    field={FormFields[FIELDS.ANNUAL_BONUS]}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'component',
                        content: (
                          <UpdatableField
                            modulePrefix={modulePrefix}
                            getErrors={getErrors}
                            fieldType={INPUTS.CHECKBOX}
                            variant="inline"
                            field={FormFields[FIELDS.HAS_ANNUAL_BONUS_GUARANTEED]}
                          />
                        ),
                      },
                    ]}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.CURRENCY}
                    field={FormFields[FIELDS.ANNUAL_COMMISSION]}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'component',
                        content: (
                          <UpdatableField
                            modulePrefix={modulePrefix}
                            getErrors={getErrors}
                            fieldType={INPUTS.CHECKBOX}
                            variant="inline"
                            field={FormFields[FIELDS.HAS_COMMISSION_GUARANTEED]}
                          />
                        ),
                      },
                    ]}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.CURRENCY}
                    field={FormFields[FIELDS.ANNUAL_OVERTIME]}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'component',
                        content: (
                          <UpdatableField
                            modulePrefix={modulePrefix}
                            getErrors={getErrors}
                            fieldType={INPUTS.CHECKBOX}
                            variant="inline"
                            field={FormFields[FIELDS.HAS_OVERTIME_GUARANTEED]}
                          />
                        ),
                      },
                    ]}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.CURRENCY}
                    field={FormFields[FIELDS.ANNUAL_SHIFT_ALLOWANCE]}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'component',
                        content: (
                          <UpdatableField
                            modulePrefix={modulePrefix}
                            getErrors={getErrors}
                            fieldType={INPUTS.CHECKBOX}
                            variant="inline"
                            field={FormFields[FIELDS.HAS_SHIFT_ALLOWANCE_GUARANTEED]}
                          />
                        ),
                      },
                    ]}
                  />
                </Box>
                <Box title="Rental Income">
                  <RentalIncomeFields getErrors={getErrors} incomes={methods.getValues()[FIELDS.RENTAL_PREFIX]} />
                </Box>
                <Box title="Fostering Income">
                  <FosteringIncomeFields getErrors={getErrors} incomes={methods.getValues()[FIELDS.FOSTERING_PREFIX]} />
                </Box>
                <Box title="Benefit Income">
                  <BenefitIncomeFields getErrors={getErrors} incomes={methods.getValues()[FIELDS.BENEFIT_PREFIX]} />
                </Box>
              </>
              <>
                <Box title="Self-employment Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.SELECT}
                    options={resources.businessTypes}
                    field={FormFields[FIELDS.BUSINESS_TYPE]}
                    required
                  />
                  {shouldShowFieldByBusinesType(
                    methods.getValues()[FIELDS.BUSINESS_TYPE],
                    FIELDS.PERCENTAGE_OF_BUSINESS_OWNED,
                  ) && (
                    <UpdatableField
                      modulePrefix={modulePrefix}
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      variant={VARIANTS_TYPES.PERCENTAGE}
                      field={FormFields[FIELDS.PERCENTAGE_OF_BUSINESS_OWNED]}
                    />
                  )}
                  {shouldShowFieldByBusinesType(
                    methods.getValues()[FIELDS.BUSINESS_TYPE],
                    FIELDS.LIMITED_COMPANY_SALARY,
                  ) && (
                    <UpdatableField
                      modulePrefix={modulePrefix}
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      variant={VARIANTS_TYPES.CURRENCY}
                      field={FormFields[FIELDS.LIMITED_COMPANY_SALARY]}
                    />
                  )}
                  {shouldShowFieldByBusinesType(
                    methods.getValues()[FIELDS.BUSINESS_TYPE],
                    FIELDS.LIMITED_COMPANY_DIVIDENDS,
                  ) && (
                    <UpdatableField
                      modulePrefix={modulePrefix}
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      variant={VARIANTS_TYPES.CURRENCY}
                      field={FormFields[FIELDS.LIMITED_COMPANY_DIVIDENDS]}
                    />
                  )}

                  {shouldShowFieldByBusinesType(methods.getValues()[FIELDS.BUSINESS_TYPE], FIELDS.NET_PROFIT) && (
                    <UpdatableField
                      modulePrefix={modulePrefix}
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      variant={VARIANTS_TYPES.CURRENCY}
                      field={FormFields[FIELDS.NET_PROFIT]}
                    />
                  )}

                  <UpdatableField
                    modulePrefix={modulePrefix}
                    getErrors={getErrors}
                    fieldType={INPUTS.TEXT}
                    variant={VARIANTS_TYPES.DATE}
                    field={FormFields[FIELDS.BUSINESS_TRADING_DATE]}
                  />
                </Box>
                <Box title="Investments Income">
                  <InvestmentsIncomeFields
                    incomes={methods.getValues()[FIELDS.INVESTMENT_PREFIX]}
                    getErrors={getErrors}
                  />
                </Box>
                <Box title="Maintenance Income">
                  <MaintenanceIncomeFields
                    incomes={methods.getValues()[FIELDS.MAINTENANCE_PREFIX]}
                    getErrors={getErrors}
                  />
                </Box>
                <Box title="Other Income">
                  <OtherIncomeFields incomes={methods.getValues()[FIELDS.OTHER_PREFIX]} getErrors={getErrors} />
                </Box>
              </>
            </Columns>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

const shouldShowFieldByBusinesType = (businessType: Nullable<string>, field: any): boolean => {
  if (!businessType) {
    return false;
  }
  const availableFields = businessTypeFieldsMap.get(businessType);
  return Boolean(availableFields && availableFields.indexOf(field) !== -1);
};

export default Current;
